/* @refresh reset */
import React, { FC, useState, useContext } from 'react';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';

import ChillerChangeHistoryRow from './ChillerChangeHistoryRow';

import { HistoryItem } from '../../../salesEstimateModules/estimateRebates/model';
import { ChillerLogsContext } from '../contexts/ChillerLogsContext';

import axios from '../../../sharedModules/utils/axios.utils';

interface ChillerFormChangeHistoryProps {
  prop1?: string;
}

const ChillerFormChangeHistory: FC<ChillerFormChangeHistoryProps> = (props) => {
  let { chillerLog } = useContext(ChillerLogsContext);

  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);

  const fetchHistoryItems = async () => {
    try {
      const response: any = await axios.get('/history_items', {
        params: {
          historyable_id: chillerLog?.id,
          historyable_type: 'ChillerLog',
        },
      });
      setHistoryItems(response.data.result);
    } catch (error) {
      console.error('Error fetching history items:', error);
    }
  };

  const handleExpand = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded && chillerLog?.id && historyItems?.length === 0) {
      fetchHistoryItems();
    }
  };

  return (
    <Card className="no-print" sx={{ mb: 3, boxShadow: 2, width: '100%', borderRadius: 0 }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Accordion
          defaultExpanded={false}
          onChange={handleExpand}
          sx={{ boxShadow: 'none', width: '100%', m: 0, '&:before': { display: 'none' } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#A21C37',
              color: 'white',
              width: '100%',
              m: 0,
              '& .MuiAccordionSummary-expandIconWrapper': {
                color: 'white',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Change History
              </Typography>
              {historyItems.length > 0 && (
                <Chip
                  label={`${historyItems.length} ${historyItems.length === 1 ? 'change' : 'changes'}`}
                  size="small"
                  sx={{ ml: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Box sx={{ p: 2 }}>
              <Grid
                container
                sx={{
                  backgroundColor: 'grey.100',
                  py: 1.5,
                  px: 2,
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                <Grid item xs={1} />
                <Grid item xs={2}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Time
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    User
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Field
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    From
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    To
                  </Typography>
                </Grid>
              </Grid>

              {historyItems.length === 0 ? (
                <Box
                  sx={{
                    py: 4,
                    textAlign: 'center',
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    border: '1px dashed',
                    borderColor: 'grey.300',
                  }}
                >
                  <HistoryIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No change history available
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {historyItems.map((historyItem, index) => (
                    <React.Fragment key={`history-item-${historyItem.id}`}>
                      <ChillerChangeHistoryRow
                        historyItem={historyItem}
                        backgroundColor={index % 2 === 0 ? 'grey.50' : 'white'}
                      />
                      {index < historyItems.length - 1 && <Divider sx={{ my: 1 }} />}
                    </React.Fragment>
                  ))}
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default ChillerFormChangeHistory;
